import type { ChangeEvent, FormEvent } from "react";
import { useState } from "react";
import type { CelebrityDifficulty, CelebrityInput, VoicePhase } from "@pushup/types";
import { ALL_DIFFICULTIES, DIFFICULTY_LABELS, DIFFICULTY_PACING } from "@pushup/types";
import { Button } from "@pushup/ui";
import { Input } from "@pushup/ui";
import { Label } from "@pushup/ui";
import { Loader2, Plus, X } from "lucide-react";

const PHASE_LIMIT = 10;

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

const IMAGE_MAX_EDGE = 512;
const IMAGE_QUALITY = 0.8;

/**
 * Downsizes the selected photo client-side so the resulting data URL stays well
 * under the backend's 500k-character schema limit for imageDataUrl.
 */
async function fileToImageDataUrl(file: File): Promise<string> {
  if (typeof createImageBitmap === "undefined") {
    return fileToDataUrl(file);
  }
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, IMAGE_MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      bitmap.close();
      return fileToDataUrl(file);
    }
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", IMAGE_QUALITY);
    bitmap.close();
    return dataUrl;
  } catch {
    return fileToDataUrl(file);
  }
}

const AUDIO_SAMPLE_RATE = 16_000;
/** Clips longer than this are downshifted to 8 kHz to fit up to ~60 s. */
const AUDIO_MAX_QUALITY_SECONDS = 30;
/** Must stay aligned with the audioDataUrl cap in @pushup/shared schemas. */
const AUDIO_DATA_URL_LIMIT = 1_400_000;

function writeAscii(view: DataView, offset: number, text: string) {
  for (let i = 0; i < text.length; i++) {
    view.setUint8(offset + i, text.charCodeAt(i));
  }
}

/** 16-bit mono PCM WAV (44-byte header + samples). */
function encodeWav(samples: Float32Array, sampleRate: number): Uint8Array {
  const dataSize = samples.length * 2;
  const bytes = new Uint8Array(44 + dataSize);
  const view = new DataView(bytes.buffer);
  writeAscii(view, 0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeAscii(view, 8, "WAVE");
  writeAscii(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  writeAscii(view, 36, "data");
  view.setUint32(40, dataSize, true);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i] ?? 0));
    view.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return bytes;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

/**
 * Re-encodes any uploaded audio (mp3/wav/m4a…) into a mono WAV so the data URL
 * stays small and uniform, regardless of the source file's format or bitrate.
 * Short clips stay at 16 kHz; longer ones drop to 8 kHz so up to ~60 seconds
 * still fits the backend cap and D1's 2 MB row limit. Throws when the clip
 * would exceed the backend's audioDataUrl cap.
 */
async function fileToAudioDataUrl(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const AudioContextCtor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextCtor) {
    return fileToDataUrl(file);
  }
  const ctx = new AudioContextCtor();
  try {
    const audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0));
    const sampleRate = audioBuffer.duration > AUDIO_MAX_QUALITY_SECONDS ? 8_000 : AUDIO_SAMPLE_RATE;
    const sampleCount = Math.ceil(audioBuffer.duration * sampleRate);
    const offline = new OfflineAudioContext(1, sampleCount, sampleRate);
    const source = offline.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(offline.destination);
    source.start();
    const rendered = await offline.startRendering();
    const wav = encodeWav(rendered.getChannelData(0), sampleRate);
    const dataUrl = `data:audio/wav;base64,${bytesToBase64(wav)}`;
    if (dataUrl.length > AUDIO_DATA_URL_LIMIT) {
      throw new Error("This clip is too long — keep it under about 60 seconds.");
    }
    return dataUrl;
  } finally {
    void ctx.close();
  }
}

export interface CelebrityFormProps {
  initial?: CelebrityInput;
  isSubmitting?: boolean;
  error?: string | null;
  onSave: (input: CelebrityInput) => void;
  onCancel?: () => void;
}

export function CelebrityForm({
  initial,
  isSubmitting = false,
  error = null,
  onSave,
  onCancel,
}: CelebrityFormProps) {
  const [name, setName] = useState(initial?.name ?? "");
  const [difficulty, setDifficulty] = useState<CelebrityDifficulty>(
    initial?.difficulty ?? "medium",
  );
  const [secondsPerRep, setSecondsPerRep] = useState(
    String(initial?.secondsPerRep ?? DIFFICULTY_PACING[difficulty]),
  );
  const [workoutDurationSec, setWorkoutDurationSec] = useState(
    String(initial?.workoutDurationSec ?? 0),
  );
  const [imageDataUrl, setImageDataUrl] = useState(initial?.imageDataUrl ?? "");
  const [audioError, setAudioError] = useState<string | null>(null);
  const [phases, setPhases] = useState<VoicePhase[]>(initial?.voicePhases ?? []);

  const handleDifficultyChange = (next: CelebrityDifficulty) => {
    setDifficulty(next);
    if (!initial) {
      setSecondsPerRep(String(DIFFICULTY_PACING[next]));
    }
  };

  const handleImageChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImageDataUrl(await fileToImageDataUrl(file));
    }
  };

  const handleAudioChange = async (index: number, file: File) => {
    try {
      const audioDataUrl = await fileToAudioDataUrl(file);
      setAudioError(null);
      setPhases((current) =>
        current.map((phase) =>
          phase.id === current[index]?.id ? { ...phase, audioDataUrl } : phase,
        ),
      );
    } catch (err) {
      setAudioError(err instanceof Error ? err.message : "Could not read this audio file.");
    }
  };

  const addPhase = () => {
    setPhases((current) => [
      ...current,
      { id: crypto.randomUUID(), name: "", audioDataUrl: "", order: current.length },
    ]);
  };

  const removePhase = (id: string) => {
    setPhases((current) =>
      current.filter((phase) => phase.id !== id).map((phase, i) => ({ ...phase, order: i })),
    );
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const seconds = Number(secondsPerRep);
    const duration = Number(workoutDurationSec);
    if (!name.trim() || !Number.isFinite(seconds) || seconds < 0.5 || !Number.isInteger(duration)) {
      return;
    }
    onSave({
      name: name.trim(),
      imageDataUrl,
      difficulty,
      secondsPerRep: seconds,
      workoutDurationSec: duration,
      voicePhases: phases.filter((phase) => phase.name.trim() && phase.audioDataUrl),
    });
  };

  return (
    <div className="widget">
      <div className="widget-body">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="celebrity-name" className="text-white/80">
              Name
            </Label>
            <Input
              id="celebrity-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="e.g. Giga Chad"
              required
              className="border-0 bg-white/5 text-white placeholder:text-white/40 focus-visible:ring-lb-blue"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="celebrity-difficulty" className="text-white/80">
                Difficulty
              </Label>
              <select
                id="celebrity-difficulty"
                value={difficulty}
                onChange={(event) =>
                  handleDifficultyChange(event.target.value as CelebrityDifficulty)
                }
                className="flex h-9 w-full rounded-md border border-white/15 bg-white/5 px-3 py-1 text-sm text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-lb-blue"
              >
                {ALL_DIFFICULTIES.map((value) => (
                  <option key={value} value={value}>
                    {DIFFICULTY_LABELS[value]}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="celebrity-seconds" className="text-white/80">
                Seconds per rep
              </Label>
              <Input
                id="celebrity-seconds"
                type="number"
                min={0.5}
                max={120}
                step={0.1}
                value={secondsPerRep}
                onChange={(event) => setSecondsPerRep(event.target.value)}
                required
                className="border-0 bg-white/5 text-white placeholder:text-white/40 focus-visible:ring-lb-blue"
              />
              <p className="text-xs text-foreground/50">Decimal steps allowed, e.g. 1.2 or 1.3</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="celebrity-duration" className="text-white/80">
                Round length (seconds)
              </Label>
              <Input
                id="celebrity-duration"
                type="number"
                min={0}
                max={3600}
                step={1}
                value={workoutDurationSec}
                onChange={(event) => setWorkoutDurationSec(event.target.value)}
                required
                className="border-0 bg-white/5 text-white placeholder:text-white/40 focus-visible:ring-lb-blue"
              />
              <p className="text-xs text-foreground/50">0 = no time limit (endless round)</p>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="celebrity-image" className="text-white/80">
              Profile image
            </Label>
            <Input
              id="celebrity-image"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="border-0 bg-white/5 text-white file:text-white"
            />
            {imageDataUrl && (
              <img src={imageDataUrl} alt="" className="mt-2 h-16 w-16 rounded-full object-cover" />
            )}
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-white/80">
                Voice phases ({phases.length}/{PHASE_LIMIT})
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addPhase}
                disabled={phases.length >= PHASE_LIMIT}
              >
                <Plus className="size-4" />
                Add phase
              </Button>
            </div>
            {phases.map((phase, index) => (
              <div
                key={phase.id}
                className="flex items-center gap-3 rounded-md border border-white/10 bg-white/5 p-3"
              >
                <Input
                  value={phase.name}
                  onChange={(event) =>
                    setPhases((current) =>
                      current.map((p) =>
                        p.id === phase.id ? { ...p, name: event.target.value } : p,
                      ),
                    )
                  }
                  placeholder={`Phase ${index + 1} label`}
                  className="max-w-40 border-0 bg-white/5 text-white placeholder:text-white/40 focus-visible:ring-lb-blue"
                />
                <Input
                  type="file"
                  accept="audio/*"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) {
                      void handleAudioChange(index, file);
                    }
                  }}
                  className="border-0 bg-white/5 text-white file:text-white"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removePhase(phase.id)}
                  aria-label={`Remove phase ${index + 1}`}
                >
                  <X className="size-4" />
                </Button>
              </div>
            ))}
            {phases.length === 0 && (
              <p className="text-sm text-foreground/50">No voice phases yet.</p>
            )}
            {audioError && <p className="text-sm text-lb-red">{audioError}</p>}
          </div>

          {error && <p className="text-sm text-lb-red">{error}</p>}

          <div className="flex justify-end gap-2">
            {onCancel && (
              <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="size-4 animate-spin" />}
              {initial ? "Save changes" : "Create celebrity"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
